import DepositInvoicePayment from '../components/DepositInvoicePayment';
import InvoicePreview from '../components/InvoicePreview';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { bookingApi, invoiceApi, contractApi, paymentApi, depositApi, receiptApi, vehicleApi, customerApi, settingsApi, letterApi, formatApiError } from '../services/api';
import EditableNumber from '../components/EditableNumber';
import VehicleSwapDialog from '../components/VehicleSwapDialog';
import CleaningPanel from '../components/CleaningPanel';
import { 
  ArrowLeft, Car, User, Calendar, MapPin, FileText, 
  Receipt, CreditCard, CheckCircle, Clock, SignIn, SignOut, EnvelopeSimple, Warning, Money, PencilSimple, ClockCounterClockwise, ArrowCounterClockwise, CurrencyEur, ArrowsLeftRight, Prohibit
} from '@phosphor-icons/react';
import SignaturePad from '../components/SignaturePad';
import RevisionHistory from '../components/RevisionHistory';
import CustomerOverrideFields from '../components/CustomerOverrideFields';
import CustomerAvatar from '../components/CustomerAvatar';
import AdditionalDriversFields from '../components/AdditionalDriversFields';
import DriverLicenseWarnings from '../components/DriverLicenseWarnings';
import BookingTasks from '../components/BookingTasks';
import ContractPreview from '../components/ContractPreview';
import BookingChangePreview from '../components/BookingChangePreview';
import { bookingEditPayload } from '../lib/bookingEditPayload';
import { euro } from '../lib/bookingChecks';
import PhotoSlotGrid from '../components/PhotoSlotGrid';
import DepositWorkflowActions, { DEPOSIT_STATUS_META } from '../components/DepositWorkflowActions';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingDetail = () => {
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState(false);
  useEffect(() => {
    let active = true;
    settingsApi.get().then(({ data }) => {
      if (active) setOnlinePaymentsEnabled(data?.online_payments_enabled === true);
    }).catch(() => { if (active) setOnlinePaymentsEnabled(false); });
    return () => { active = false; };
  }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [relatedLetters, setRelatedLetters] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [bookingInvoices, setBookingInvoices] = useState([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const invoiceCreationPending = useRef(false);
  const [loading, setLoading] = useState(true);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [depositPaymentInvoice, setDepositPaymentInvoice] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [deposits, setDeposits] = useState([]);
  const [depositForm, setDepositForm] = useState({
    amount: '',
    description: 'Kaution',
    payment_method: 'bar',
    notes: ''
  });
  const [depositSignature, setDepositSignature] = useState(null);
  const [showContractPreview, setShowContractPreview] = useState(false);
  const [changePreview, setChangePreview] = useState(null);
  const [savingBooking, setSavingBooking] = useState(false);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);
  const [depositsLoaded, setDepositsLoaded] = useState(false);
  const [showEditBooking, setShowEditBooking] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [showTerminate, setShowTerminate] = useState(false);
  const [termReason, setTermReason] = useState('agb_verstoss');
  const [termNotes, setTermNotes] = useState('');
  const [termMileage, setTermMileage] = useState('');
  const [terminating, setTerminating] = useState(false);
  const [showTerminateEmail, setShowTerminateEmail] = useState(false);
  const [sendingTerminateEmail, setSendingTerminateEmail] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editBookingForm, setEditBookingForm] = useState({
    customer_id: '',
    customer_override: null,
    vehicle_id: '',
    start_date: '',
    end_date: '',
    pickup_location: '',
    return_location: '',
    pickup_mileage: '',
    deposit_amount: '',
    insurance_deductible: '',
    use_weekend_rate: false,
    notes: '',
    manual_daily_rate: '',
    manual_base_price: '',
    manual_extras_price: '',
    manual_total_price: '',
    manual_price_note: '',
    included_km: '',
    extra_km_rate: '',
  });

  const [checkInData, setCheckInData] = useState({
    mileage_start: '',
    fuel_level_start: 'voll',
    condition_notes: '',
    performed_at: '',
    agb_acknowledged: false,
    privacy_acknowledged: false,
    agb_version: '2026-01',
    privacy_version: '2026-01',
    acknowledged_at: null,
  });
  const [checkInPhotos, setCheckInPhotos] = useState([]);
  const [checkInSigCustomer, setCheckInSigCustomer] = useState(null);
  const [checkInSigLandlord, setCheckInSigLandlord] = useState(null);
  const [complianceSignature, setComplianceSignature] = useState(null);
  const [checkInSigShow, setCheckInSigShow] = useState(null); // 'customer' | 'landlord' | null
  // Live state of structured photo slots for current Check-In/-Out dialog
  const [photoSlots, setPhotoSlots] = useState(null);
  const [livePhotosCheckIn, setLivePhotosCheckIn] = useState([]);
  const [livePhotosCheckOut, setLivePhotosCheckOut] = useState([]);

  const [checkOutData, setCheckOutData] = useState({
    mileage_end: '',
    fuel_level_end: 'voll',
    condition_notes: '',
    damage_notes: '',
    performed_at: '',
    km_kulanz: false,
  });
  const [checkOutPhotos, setCheckOutPhotos] = useState([]);
  const [checkOutSigCustomer, setCheckOutSigCustomer] = useState(null);
  const [checkOutSigLandlord, setCheckOutSigLandlord] = useState(null);
  const [checkOutSigShow, setCheckOutSigShow] = useState(null);

  const [paymentData, setPaymentData] = useState({
    idempotency_key: crypto.randomUUID(),
    amount: '',
    method: 'bar',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    loadBooking();
    // Load photo slot configuration (once per mount is enough)
    settingsApi.getPhotoSlots()
      .then((r) => setPhotoSlots(r.data?.slots || []))
      .catch(() => setPhotoSlots(null));

    // Check for payment return
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (payment === 'success' && sessionId) {
      pollPaymentStatus(sessionId);
    }

    // Quick-Action from Dashboard: auto-open check-in/check-out dialog
    const action = searchParams.get('action');
    if (action === 'checkin') setShowCheckInModal(true);
    if (action === 'checkout') setShowCheckOutModal(true);
  }, [id, searchParams]);

  // Auto-prefill Km-Stand beim Öffnen des Übergabe-Dialogs mit letztem bekannten Fahrzeug-Kilometerstand
  useEffect(() => {
    if (showCheckInModal && booking?.vehicle?.current_mileage != null && !checkInData.mileage_start) {
      setCheckInData((prev) => ({ ...prev, mileage_start: String(booking.vehicle.current_mileage) }));
    }
  }, [showCheckInModal, booking]);

  const loadBooking = async () => {
    setInvoicesLoaded(false);
    setDepositsLoaded(false);
    try {
      const response = await bookingApi.getOne(id);
      setBooking(response.data);
      // Sync live photo state with what's already on the booking
      setLivePhotosCheckIn(response.data?.check_in?.photos || []);
      setLivePhotosCheckOut(response.data?.check_out?.photos || []);
      
      // Try to load the main rental invoice.
      // A pure Mehrkilometer invoice must not block creation of the
      // actual vehicle-rental invoice.
      try {
        const invoicesRes = await invoiceApi.getAll({ booking_id: id });
        const bookingInvoices = (invoicesRes.data || []).filter(
          inv => inv.booking_id === id && inv.status !== 'cancelled'
        );

        setBookingInvoices(bookingInvoices);
        setInvoicesLoaded(true);

        const bookingInvoice = bookingInvoices.find(inv =>
          (inv.line_items || []).some(item =>
            String(item.description || '').startsWith('Fahrzeugmiete:')
          )
        );

        if (bookingInvoice) {
          const fullInvoice = await invoiceApi.getOne(bookingInvoice.id);
          setInvoice(fullInvoice.data);
        } else {
          setInvoice(null);
        }
      } catch (e) {
        setInvoice(null);
      }

      // Zugehörige Briefe laden
      try {
        const lettersRes = await letterApi.list({ related_type: 'booking', related_id: id });
        setRelatedLetters(lettersRes.data || []);
      } catch (e) {
        setRelatedLetters([]);
      }

      // Load deposits for this booking
      try {
        const depRes = await depositApi.getAll({});
        setDeposits((depRes.data || []).filter(d => d.booking_id === id));
        setDepositsLoaded(true);
      } catch (e) {
        // ignore
      }
    } catch (error) {
      toast.error(formatApiError(error));
      navigate('/bookings');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info('Zahlungsstatus wird überprüft...');
      return;
    }
    
    try {
      const response = await paymentApi.getStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success('Zahlung erfolgreich!');
        loadBooking();
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    // Rückfrage: Km-Stand bestätigen
    const enteredKm = parseInt(checkInData.mileage_start);
    if (isNaN(enteredKm)) {
      toast.error('Bitte gültigen Km-Stand eingeben');
      return;
    }
    const refKm = booking?.vehicle?.current_mileage;
    const kmMsg = refKm != null && enteredKm !== parseInt(refKm)
      ? `Km-Stand: ${enteredKm.toLocaleString('de-DE')} km\nLetzter bekannter Stand: ${parseInt(refKm).toLocaleString('de-DE')} km\nAbweichung: ${(enteredKm - parseInt(refKm)).toLocaleString('de-DE')} km\n\nStimmt der eingegebene Kilometerstand?`
      : `Kilometerstand: ${enteredKm.toLocaleString('de-DE')} km\n\nStimmt der Kilometerstand?`;
    if (!window.confirm(kmMsg)) {
      // Fokus auf Km-Feld für manuelle Korrektur
      setTimeout(() => {
        const el = document.querySelector('[data-testid="checkin-mileage-input"]');
        if (el) { el.focus(); el.select?.(); }
      }, 100);
      return;
    }
    setCheckInData(prev => ({
      ...prev,
      mileage_start: enteredKm,
    }));
    setShowComplianceModal(true);
  };

  const handleCheckOut = async (e) => {
    e.preventDefault();
    try {
      const res = await bookingApi.checkOut(id, {
        ...checkOutData,
        mileage_end: parseInt(checkOutData.mileage_end),
        signature_customer: checkOutSigCustomer,
        signature_landlord: checkOutSigLandlord,
      });
      // Photos are uploaded live by PhotoSlotGrid before submit.
      toast.success('Rückgabe erfolgreich');
      // Auto-Mehrkilometer-Position wurde ggf. serverseitig angelegt
      const km = res?.data?.extra_km_charge;
      if (km?.added_line) {
        toast.success(
          `Mehrkilometer automatisch berechnet: ${km.added_line.quantity} km × € ${Number(km.added_line.unit_price).toFixed(2)} = € ${Number(km.added_line.total).toFixed(2)}`,
          {
            description: km.action === 'appended'
              ? 'Position zur bestehenden Rechnung hinzugefügt.'
              : 'Neue Nachberechnungs-Rechnung erstellt.',
            duration: 10000,
          }
        );
      }
      setShowCheckOutModal(false);
      setCheckOutPhotos([]);
      setCheckOutSigCustomer(null);
      setCheckOutSigLandlord(null);
      setCheckOutSigShow(null);
      loadBooking();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleUndoCheckIn = async () => {
    if (!window.confirm('Übergabe wirklich rückgängig machen? Die eingegebenen Daten gehen verloren.')) return;
    try {
      await bookingApi.undoCheckIn(id);
      toast.success('Übergabe rückgängig gemacht');
      loadBooking();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleUndoCheckOut = async () => {
    if (!window.confirm('Rückgabe wirklich rückgängig machen? Die eingegebenen Daten gehen verloren.')) return;
    try {
      await bookingApi.undoCheckOut(id);
      toast.success('Rückgabe rückgängig gemacht');
      loadBooking();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const downloadAccidentReport = async () => {
    try {
      const response = await bookingApi.getAccidentReportPdf(id);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const reference = booking?.contract_number || booking?.booking_number;
      link.download = (reference ? `Unfallbericht_${reference}.pdf` : 'Unfallbericht.pdf').replace(/[\\/:*?"<>|]/g, '_');
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const downloadProtocol = async () => {
    try {
      const response = await bookingApi.getProtocolPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `protokoll_${id.substring(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const createInvoice = async () => {
    if (invoiceCreationPending.current) return;
    invoiceCreationPending.current = true;
    setCreatingInvoice(true);
    try {
      const response = await invoiceApi.create({ booking_id: id });
      const created = response.data;
      setInvoice(created);
      setBookingInvoices(previous => [created, ...previous.filter(item => item.id !== created.id)]);
      setInvoicesLoaded(true);
      toast.success('Rechnung erstellt');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      invoiceCreationPending.current = false;
      setCreatingInvoice(false);
    }
  };

  const handleDepositPaymentRecorded = (result) => {
    const updateInvoice = current => current?.id === result.invoice_id ? {
      ...current,
      paid_amount: result.paid_amount,
      status: result.invoice_status,
      open_amount: result.open_amount,
      deposit_paid_amount: result.deposit_paid_amount,
    } : current;
    setBookingInvoices(previous => previous.map(updateInvoice));
    setInvoice(updateInvoice);
    setInvoicesLoaded(true);
    if (result.deposit) {
      setDeposits(previous => previous.map(current => current.id === result.deposit.id ? { ...current, ...result.deposit } : current));
    }
  };

  const handleManualPayment = async (e) => {
    e.preventDefault();
    const targetInvoice = paymentInvoice || invoice;
    if (!targetInvoice?.id) {
      toast.error('Keine Rechnung ausgewählt');
      return;
    }

    try {
      await invoiceApi.recordPayment(targetInvoice.id, {
        ...paymentData,
        amount: parseFloat(paymentData.amount)
      });
      toast.success('Zahlung erfasst');
      setShowPaymentModal(false);
      setPaymentInvoice(null);
      loadBooking();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleStripePayment = async () => {
    try {
      const origin = window.location.origin;
      const response = await paymentApi.createCheckout(id, origin);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const downloadContract = async () => {
    try {
      const response = await contractApi.getPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers?.['content-disposition'] || '';
      const serverFilename = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      a.download = (serverFilename || `vertrag_${booking?.contract_number || booking?.booking_number || id.substring(0, 8)}.pdf`).replace(/[\\/:*?"<>|]/g, '_');
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleSendConfirmation = async () => {
    try {
      const response = await bookingApi.sendConfirmation(id);
      if (response.data.sent) {
        toast.success('Buchungsbestätigung wurde gesendet');
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openEditBooking = async () => {
    if (vehicles.length === 0) {
      try {
        const res = await vehicleApi.getAll();
        setVehicles(res.data || []);
      } catch { /* ignore */ }
    }
    if (customers.length === 0) {
      try {
        const res = await customerApi.getAll();
        setCustomers(res.data || []);
      } catch { /* ignore */ }
    }
    setEditBookingForm({
      customer_id: booking?.customer_id || '',
      customer_override: booking?.customer_override || null,
      vehicle_id: booking?.vehicle_id || '',
      start_date: (booking?.start_date || '').substring(0, 10),
      end_date: (booking?.end_date || '').substring(0, 10),
      pickup_location: booking?.pickup_location || '',
      return_location: booking?.return_location || '',
      pickup_mileage: booking?.pickup_mileage ?? '',
      deposit_amount: booking?.deposit_amount ?? '',
      insurance_deductible: booking?.insurance_deductible ?? '',
      use_weekend_rate: !!booking?.use_weekend_rate,
      notes: booking?.notes || '',
      additional_drivers: booking?.additional_drivers || [],
      manual_daily_rate: '',
      manual_base_price: '',
      manual_extras_price: '',
      manual_total_price: booking?.price_info?.manual_override ? (booking?.price_info?.total_price ?? '') : '',
      manual_price_note: booking?.price_info?.manual_price_note || '',
      included_km: booking?.included_km ?? '',
      extra_km_rate: booking?.extra_km_rate ?? '',
    });
    setChangePreview(null);
    setShowEditBooking(true);
  };

  const submitEditBooking = async (e) => {
    e.preventDefault();
    if (savingBooking) return;
    try {
      const payload = bookingEditPayload(editBookingForm);
      if (payload.end_date !== booking.end_date?.slice(0, 10)) {
        if (!changePreview || changePreview.key !== JSON.stringify(payload)) {
          toast.error('Bitte warten, bis die aktuelle Kostenübersicht geladen ist.');
          return;
        }
        const preview = changePreview.data;
        const message = preview.extension
          ? `${preview.additional_days} zusätzliche Miettage: ${euro(preview.extension.total)} als separate Nachberechnung. Bestehende Rechnungen bleiben erhalten.`
          : preview.shortening ? 'Die Mietzeit wird verkürzt. Offene Verlängerungsrechnungen werden geprüft; bezahlte Rechnungen müssen zuvor geklärt werden.'
          : `Neuer Buchungspreis: ${euro(preview.price_info.total_price)}. Es wird keine separate Verlängerungsrechnung erstellt.`;
        if (!window.confirm(`${message}\n\nÄnderung speichern?`)) return;
      }
      setSavingBooking(true);
      await bookingApi.update(id, payload);
      toast.success('Buchung aktualisiert');
      setShowEditBooking(false);
      loadBooking();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSavingBooking(false);
    }
  };

  const resetManualPrice = async () => {
    if (!window.confirm('Manuelle Preise zurücksetzen und automatisch neu berechnen?')) return;
    try {
      await bookingApi.update(id, { reset_manual_price: true });
      toast.success('Preis zurückgesetzt auf automatische Berechnung');
      loadBooking();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openDepositModal = () => {
    setDepositForm({
      amount: booking?.deposit_amount ? String(booking.deposit_amount) : '',
      description: 'Kaution',
      payment_method: 'bar',
      notes: ''
    });
    setDepositSignature(null);
    setShowDepositModal(true);
  };

  const downloadReceiptPdf = async (receiptId, receiptNumber) => {
    try {
      const res = await receiptApi.getPdf(receiptId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quittung_${receiptNumber || receiptId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitDeposit = async (e) => {
    e.preventDefault();
    if (!booking?.customer_id || !depositForm.amount) return;
    try {
      const depRes = await depositApi.create({
        customer_id: booking.customer_id,
        booking_id: id,
        amount: parseFloat(depositForm.amount),
        description: depositForm.description || 'Kaution',
        payment_method: depositForm.payment_method,
        notes: depositForm.notes || null,
      });
      const newDeposit = depRes.data;

      // Quittung mit Unterschrift automatisch erstellen
      const rcpt = await receiptApi.create({
        customer_id: booking.customer_id,
        deposit_id: newDeposit.id,
        amount: parseFloat(depositForm.amount),
        description: `Kaution hinterlegt (Buchung ${booking.booking_number || id.substring(0, 8)})`,
        payment_method: depositForm.payment_method,
        signature_data: depositSignature,
        notes: depositForm.notes || null,
      });

      toast.success(
        depositForm.payment_method === 'bar'
          ? 'Kaution hinterlegt · Kassenbuch + Quittung erstellt'
          : 'Kaution hinterlegt · Quittung erstellt'
      );
      setShowDepositModal(false);
      loadBooking();
      // Quittungs-PDF direkt herunterladen
      if (rcpt?.data?.id) {
        downloadReceiptPdf(rcpt.data.id, rcpt.data.receipt_number);
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const downloadInvoice = async (targetInvoice = invoice) => {
    if (!targetInvoice?.id) {
      toast.error('Keine Rechnung ausgewählt');
      return;
    }

    try {
      const response = await invoiceApi.getPdf(targetInvoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rechnung_${targetInvoice.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'badge-warning',
      active: 'badge-success',
      completed: 'badge-neutral',
      cancelled: 'badge-error',
      terminated: 'badge-error',
    };
    const labels = {
      confirmed: 'Bestätigt',
      active: 'Aktiv',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert',
      terminated: 'Gekündigt',
    };
    return <span className={`badge ${styles[status] || 'badge-neutral'}`}>{labels[status] || status}</span>;
  };

  const TERMINATION_REASON_OPTIONS = [
    { value: 'agb_verstoss', label: 'AGB-Verstoß' },
    { value: 'zahlungsverzug', label: 'Zahlungsverzug' },
    { value: 'schaeden', label: 'Fahrzeugschäden / unsachgemäßer Gebrauch' },
    { value: 'kein_rueckruf', label: 'Kein Rückruf / nicht erreichbar' },
    { value: 'unfall_verschulden', label: 'Verschuldeter Unfall' },
    { value: 'sonstige', label: 'Sonstige' },
  ];

  const handleTerminate = async (e) => {
    e.preventDefault();
    if (!window.confirm('Fristlose Kündigung wirklich durchführen? Der Mietvertrag wird sofort beendet und das Fahrzeug freigegeben.')) return;
    setTerminating(true);
    try {
      await bookingApi.terminate(id, {
        reason: termReason,
        notes: termNotes.trim() || null,
        mileage_at_termination: termMileage ? parseInt(termMileage) : null,
        require_immediate_return: true,
      });
      toast.success('Vertrag fristlos gekündigt');
      setShowTerminate(false);
      setTermReason('agb_verstoss');
      setTermNotes('');
      setTermMileage('');
      await loadBooking();
      // Folge-Dialog: E-Mail versenden?
      setShowTerminateEmail(true);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setTerminating(false);
    }
  };

  const downloadTerminationPdf = async () => {
    try {
      const response = await bookingApi.getTerminationPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kuendigung_${(booking?.contract_number || booking?.booking_number || id.substring(0, 8))}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Kündigungs-PDF heruntergeladen');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const sendTerminationEmail = async () => {
    setSendingTerminateEmail(true);
    try {
      const res = await bookingApi.sendTerminationEmail(id);
      toast.success(res.data?.message || 'E-Mail versendet');
      setShowTerminateEmail(false);
      await loadBooking();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSendingTerminateEmail(false);
    }
  };


  const sendRegistrationDocumentEmail = async () => {
    const customerName = `${booking?.customer?.first_name || ''} ${booking?.customer?.last_name || ''}`.trim();
    const customerEmail = booking?.customer?.email || '';

    if (!customerEmail) {
      toast.error('Für diesen Kunden ist keine E-Mail-Adresse hinterlegt');
      return;
    }

    const confirmed = window.confirm(
      `Fahrzeugschein an ${customerName || 'den Kunden'} senden?\n\nE-Mail: ${customerEmail}`
    );

    if (!confirmed) return;

    try {
      const res = await bookingApi.sendRegistrationDocumentEmail(id);

      if (res.data?.email_sent) {
        toast.success(`Fahrzeugschein wurde an ${res.data.recipient} gesendet`);
      } else {
        toast.error(res.data?.message || 'Fahrzeugschein konnte nicht versendet werden');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="h-8 w-48 skeleton rounded mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-swiss h-64 skeleton" />
            <div className="card-swiss h-48 skeleton" />
          </div>
          <div className="card-swiss h-96 skeleton" />
        </div>
      </div>
    );
  }

  if (!booking) return null;

  return (
    <div className="p-6 space-y-6" data-testid="booking-detail-page">
      <BookingTasks booking={booking} deposits={deposits} invoices={bookingInvoices} slots={photoSlots} depositsLoaded={depositsLoaded} invoicesLoaded={invoicesLoaded}
        onEdit={openEditBooking} onDeposit={() => document.getElementById('booking-deposits')?.scrollIntoView({ behavior: 'smooth' })}
        onCheckIn={() => setShowCheckInModal(true)} onCheckOut={() => setShowCheckOutModal(true)} onPreview={() => setShowContractPreview(true)}
        onInvoices={() => document.getElementById('booking-invoices')?.scrollIntoView({ behavior: 'smooth' })} />
      <ContractPreview bookingId={id} open={showContractPreview} onOpenChange={setShowContractPreview} onDownload={downloadContract} />

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/bookings')} className="p-2">
          <ArrowLeft weight="bold" size={20} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-[#111418]">
            Buchung {booking.booking_number || `#${booking.id.substring(0, 8).toUpperCase()}`}
          </h1>
          <p className="text-[#525866] flex items-center gap-3 flex-wrap">
            <span>Erstellt am {new Date(booking.created_at).toLocaleDateString('de-DE')}</span>
            <span className="text-xs px-2 py-0.5 bg-[#F1F2F4] rounded flex items-center gap-1">
              Buchungs-Nr.:
              <EditableNumber
                value={booking.booking_number}
                label="Buchungsnummer"
                testId="booking-number"
                small
                onSave={async (newVal) => {
                  const res = await bookingApi.update(booking.id, { booking_number: newVal });
                  setBooking(res.data);
                }}
              />
            </span>
            {booking.contract_number && (
              <span className="text-xs px-2 py-0.5 bg-[#F1F2F4] rounded flex items-center gap-1">
                Mietvertrag:
                <EditableNumber
                  value={booking.contract_number}
                  label="Mietvertrag-Nummer"
                  testId="contract-number"
                  small
                  onSave={async (newVal) => {
                    const res = await bookingApi.update(booking.id, { contract_number: newVal });
                    setBooking(res.data);
                  }}
                />
              </span>
            )}
          </p>
        </div>
        {getStatusBadge(booking.status)}
        <Button
          variant="outline"
          onClick={() => setShowHistory(true)}
          data-testid="booking-history-btn"
          title="Änderungsverlauf"
        >
          <ClockCounterClockwise weight="bold" size={16} className="mr-2" />
          Verlauf
        </Button>
        <Button
          variant="outline"
          onClick={openEditBooking}
          data-testid="edit-booking-btn"
        >
          <PencilSimple weight="bold" size={16} className="mr-2" />
          Bearbeiten
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer & Vehicle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card-swiss p-4">
              <div className="flex items-center gap-3 mb-3">
                <CustomerAvatar customer={booking.customer} size={40} />
                <div>
                  <p className="overline">Kunde</p>
                  <p className="font-bold text-[#111418]">
                    {booking.customer?.first_name} {booking.customer?.last_name}
                  </p>
                </div>
              </div>
              <div className="text-sm text-[#525866] space-y-1">
                <p>{booking.customer?.email}</p>
                <p>{booking.customer?.phone}</p>
              </div>
            </div>

            <div className="card-swiss p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
                  <Car weight="fill" size={20} className="text-[#E60000]" />
                </div>
                <div>
                  <p className="overline">Fahrzeug</p>
                  <p className="font-bold text-[#111418]">
                    {booking.vehicle?.brand} {booking.vehicle?.model}
                  </p>
                </div>
              </div>
              <div className="text-sm text-[#525866] space-y-1">
                <p>{booking.vehicle?.license_plate}</p>
                <p>€{booking.vehicle?.daily_rate}/Tag</p>
              </div>
            </div>
          </div>

          {/* Booking Details */}
          <div className="card-swiss p-4">
            <h3 className="font-bold text-[#111418] mb-4">Buchungsdetails</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Calendar weight="bold" size={20} className="text-[#8A94A6]" />
                <div>
                  <p className="text-xs text-[#8A94A6]">Zeitraum</p>
                  <p className="font-medium">
                    {new Date(booking.start_date).toLocaleDateString('de-DE')} - {new Date(booking.end_date).toLocaleDateString('de-DE')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock weight="bold" size={20} className="text-[#8A94A6]" />
                <div>
                  <p className="text-xs text-[#8A94A6]">Dauer</p>
                  <p className="font-medium">{booking.price_info?.days} Tage</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin weight="bold" size={20} className="text-[#8A94A6]" />
                <div>
                  <p className="text-xs text-[#8A94A6]">Abholung</p>
                  <p className="font-medium">
                    {booking.pickup_station ? booking.pickup_station.name : (booking.pickup_location || '—')}
                  </p>
                  {booking.pickup_station && (
                    <p className="text-xs text-[#8A94A6]">
                      {booking.pickup_station.address}, {booking.pickup_station.postal_code} {booking.pickup_station.city}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin weight="bold" size={20} className="text-[#8A94A6]" />
                <div>
                  <p className="text-xs text-[#8A94A6]">Rückgabe</p>
                  <p className="font-medium">
                    {booking.return_station ? booking.return_station.name : (booking.return_location || '—')}
                  </p>
                  {booking.return_station && (
                    <p className="text-xs text-[#8A94A6]">
                      {booking.return_station.address}, {booking.return_station.postal_code} {booking.return_station.city}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {(booking.deposit_amount > 0 || booking.insurance_deductible > 0 || booking.pickup_mileage != null) && (
              <div className="mt-4 pt-4 border-t border-[#E2E4E9] grid grid-cols-1 sm:grid-cols-3 gap-4">
                {booking.deposit_amount > 0 && (
                  <div>
                    <p className="text-xs text-[#8A94A6]">Kaution</p>
                    <p className="font-semibold text-[#E60000]">€ {Number(booking.deposit_amount).toFixed(2)}</p>
                  </div>
                )}
                {booking.insurance_deductible > 0 && (
                  <div>
                    <p className="text-xs text-[#8A94A6]">Selbstbehalt Vollkasko</p>
                    <p className="font-semibold text-[#E60000]">€ {Number(booking.insurance_deductible).toFixed(2)}</p>
                  </div>
                )}
                {booking.pickup_mileage != null && (
                  <div>
                    <p className="text-xs text-[#8A94A6]">Km-Stand Übergabe</p>
                    <p className="font-semibold">{Number(booking.pickup_mileage).toLocaleString('de-DE')} km</p>
                  </div>
                )}
              </div>
            )}

            {booking.extras?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#E2E4E9]">
                <p className="text-xs text-[#8A94A6] mb-2">Extras</p>
                <div className="flex flex-wrap gap-2">
                  {booking.extras.map(extra => (
                    <span key={extra} className="badge badge-neutral capitalize">{extra}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Übergabe/Rückgabe */}
          <div className="card-swiss p-4">
            <h3 className="font-bold text-[#111418] mb-4">Übergabe / Rückgabe</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-4 rounded-sm border ${booking.check_in ? 'border-[#009951] bg-green-50' : 'border-[#E2E4E9]'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <SignOut weight="bold" size={20} className={booking.check_in ? 'text-[#009951]' : 'text-[#8A94A6]'} />
                  <span className="font-semibold">Übergabe</span>
                </div>
                {booking.check_in ? (
                  <div className="text-sm space-y-1">
                    <p>Kilometerstand: {booking.check_in.mileage_start} km</p>
                    <p>Tankstand: {booking.check_in.fuel_level_start}</p>
                    <p className="text-xs text-[#8A94A6]">
                      {new Date(booking.check_in.performed_at).toLocaleString('de-DE')}
                    </p>
                    {!booking.check_out && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleUndoCheckIn}
                        className="mt-2 text-[#E60000] border-[#E60000] hover:bg-red-50"
                        data-testid="undo-check-in-btn"
                      >
                        <ArrowCounterClockwise weight="bold" size={14} className="mr-1" />
                        Rückgängig
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button
                    onClick={() => setShowCheckInModal(true)}
                    className="btn-primary w-full mt-2"
                    disabled={booking.status !== 'confirmed'}
                    data-testid="check-in-btn"
                  >
                    Übergabe durchführen
                  </Button>
                )}
              </div>

              <div className={`p-4 rounded-sm border ${booking.check_out ? 'border-[#009951] bg-green-50' : 'border-[#E2E4E9]'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <SignIn weight="bold" size={20} className={booking.check_out ? 'text-[#009951]' : 'text-[#8A94A6]'} />
                  <span className="font-semibold">Rückgabe</span>
                </div>
                {booking.check_out ? (
                  <div className="text-sm space-y-1">
                    <p>Kilometerstand: {booking.check_out.mileage_end} km</p>
                    <p>Tankstand: {booking.check_out.fuel_level_end}</p>
                    <p className="text-xs text-[#8A94A6]">
                      {new Date(booking.check_out.performed_at).toLocaleString('de-DE')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUndoCheckOut}
                      className="mt-2 text-[#E60000] border-[#E60000] hover:bg-red-50"
                      data-testid="undo-check-out-btn"
                    >
                      <ArrowCounterClockwise weight="bold" size={14} className="mr-1" />
                      Rückgängig
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => setShowCheckOutModal(true)}
                    className="btn-secondary w-full mt-2"
                    disabled={booking.status !== 'active'}
                    data-testid="check-out-btn"
                  >
                    Rückgabe durchführen
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="card-swiss p-4 mt-4" data-testid="additional-drivers-summary">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold">Zusatzfahrer</h3>
              <Button type="button" variant="outline" size="sm" onClick={openEditBooking}>Bearbeiten</Button>
            </div>
            {(booking.additional_drivers || []).length === 0 ? <p className="text-sm text-[#8A94A6] mt-2">Keine Zusatzfahrer eingetragen.</p> : (
              <ul className="mt-2 space-y-2">{booking.additional_drivers.map((driver, index) => (
                <li key={index} className="text-sm">
                  <p className="font-medium">{driver.first_name} {driver.last_name}</p>
                  {driver.date_of_birth && <p>Geburtsdatum: {new Date(driver.date_of_birth).toLocaleDateString('de-DE')}</p>}
                  {driver.address && <p>{driver.address}</p>}
                  {driver.driver_license_number && <p>Führerschein: {driver.driver_license_number}</p>}
                  {driver.driver_license_expiry && <p>Gültig bis: {new Date(driver.driver_license_expiry).toLocaleDateString('de-DE')}</p>}
                </li>
              ))}</ul>
            )}
          </div>

          {/* Fahrzeug-Tausch während aktiver Miete */}
          {booking.status === 'active' && (
            <div className="card-swiss p-4 mt-4" data-testid="vehicle-swap-cta">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-[#FFB800]/10 flex items-center justify-center">
                    <ArrowsLeftRight weight="bold" size={18} className="text-[#FFB800]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#111418]">Fahrzeug-Tausch</p>
                    <p className="text-xs text-[#8A94A6]">Defekt, Unfall oder Wartung? Ersatzfahrzeug zuweisen, ohne den Vertrag zu beenden.</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="border-[#FFB800] text-[#FFB800] hover:bg-[#FFB800]/10"
                  onClick={() => setShowSwapDialog(true)}
                  data-testid="swap-vehicle-btn"
                >
                  <ArrowsLeftRight weight="bold" size={14} className="mr-1.5" />
                  Fahrzeug tauschen
                </Button>
              </div>
            </div>
          )}

          {/* Fristlose Kündigung (AGB-Verstoß, Zahlungsverzug etc.) */}
          {(booking.status === 'active' || booking.status === 'confirmed') && (
            <div className="card-swiss p-4 mt-4 border-l-4 border-l-[#E60000]" data-testid="terminate-cta">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-[#E60000]/10 flex items-center justify-center">
                    <Prohibit weight="bold" size={18} className="text-[#E60000]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#111418]">Fristlose Kündigung</p>
                    <p className="text-xs text-[#8A94A6]">Bei AGB-Verstoß, Zahlungsverzug oder verschuldetem Unfall den Vertrag sofort beenden.</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="border-[#E60000] text-[#E60000] hover:bg-[#E60000]/10"
                  onClick={() => setShowTerminate(true)}
                  data-testid="terminate-booking-btn"
                >
                  <Prohibit weight="bold" size={14} className="mr-1.5" />
                  Vertrag kündigen
                </Button>
              </div>
            </div>
          )}

          {/* Kündigungs-Info, falls Buchung gekündigt wurde */}
          {booking.status === 'terminated' && booking.termination && (
            <div className="card-swiss p-4 mt-4 border-l-4 border-l-[#E60000] bg-[#FFF5F5]" data-testid="termination-info">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-[#E60000]/10 flex items-center justify-center shrink-0">
                  <Prohibit weight="bold" size={18} className="text-[#E60000]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#E60000]">Vertrag fristlos gekündigt</p>
                  <p className="text-sm text-[#111418] mt-1">
                    <span className="font-semibold">Grund:</span> {booking.termination.reason_label || booking.termination.reason}
                  </p>
                  <p className="text-xs text-[#525866] mt-1">
                    {booking.termination.terminated_at ? new Date(booking.termination.terminated_at).toLocaleString('de-DE') : '—'}
                    {booking.termination.terminated_by_name && ` · durch ${booking.termination.terminated_by_name}`}
                  </p>
                  {booking.termination.mileage_at_termination != null && (
                    <p className="text-xs text-[#525866] mt-0.5 font-mono">
                      End-km bei Kündigung: {Number(booking.termination.mileage_at_termination).toLocaleString('de-DE')} km
                    </p>
                  )}
                  {booking.termination.notes && (
                    <p className="text-sm text-[#111418] mt-2 bg-white rounded-sm px-3 py-2 border border-[#FFD4D4]" data-testid="termination-notes">
                      {booking.termination.notes}
                    </p>
                  )}
                  {booking.termination.email_sent_at && (
                    <p className="text-xs text-[#009951] mt-2" data-testid="termination-email-status">
                      <EnvelopeSimple size={12} weight="bold" className="inline mr-1" />
                      E-Mail gesendet an {booking.termination.email_sent_to} · {new Date(booking.termination.email_sent_at).toLocaleString('de-DE')}
                      {!booking.termination.email_delivered && ' (MOCKED — SMTP nicht konfiguriert)'}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadTerminationPdf}
                      data-testid="download-termination-pdf-btn"
                    >
                      <FileText size={14} weight="bold" className="mr-1.5" />
                      Kündigungs-PDF
                    </Button>
                    {booking.customer?.email && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowTerminateEmail(true)}
                        data-testid="resend-termination-email-btn"
                      >
                        <EnvelopeSimple size={14} weight="bold" className="mr-1.5" />
                        {booking.termination.email_sent_at ? 'E-Mail erneut senden' : 'Per E-Mail versenden'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tausch-Historie */}
          {Array.isArray(booking.vehicle_swaps) && booking.vehicle_swaps.length > 0 && (
            <div className="card-swiss p-4 mt-4" data-testid="vehicle-swap-history">
              <h3 className="font-bold text-[#111418] mb-3 flex items-center gap-2">
                <ArrowsLeftRight size={16} weight="bold" className="text-[#FFB800]" />
                Fahrzeug-Tausch-Historie ({booking.vehicle_swaps.length})
              </h3>
              <ol className="space-y-2.5">
                {booking.vehicle_swaps.map((s, idx) => (
                  <li
                    key={s.id || idx}
                    className="border-l-2 border-[#FFB800] pl-3 py-1"
                    data-testid={`swap-history-item-${idx}`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold text-[#111418]">
                        {s.from_vehicle_label} <span className="text-[#FFB800]">→</span> {s.to_vehicle_label}
                      </p>
                      <span className="text-xs px-2 py-0.5 bg-[#FFF7E6] text-[#B8860B] rounded-sm">
                        {s.reason_label || s.reason}
                      </span>
                    </div>
                    <p className="text-xs text-[#525866] mt-1">
                      {s.swapped_at ? new Date(s.swapped_at).toLocaleString('de-DE') : '—'}
                      {s.swapped_by_name && ` · ${s.swapped_by_name}`}
                    </p>
                    {(s.from_mileage_end != null || s.to_mileage_start != null) && (
                      <div className="text-xs text-[#525866] mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div className="bg-[#F7F8FA] rounded px-2 py-1">
                          <p className="text-[10px] text-[#8A94A6] uppercase">Gefahren (altes Auto)</p>
                          <p className="font-mono font-bold text-[#111418]">
                            {(s.from_driven_km ?? 0).toLocaleString('de-DE')} km
                          </p>
                          <p className="text-[10px] text-[#8A94A6]">
                            {s.from_mileage_start?.toLocaleString('de-DE') ?? '—'} → {s.from_mileage_end?.toLocaleString('de-DE') ?? '—'}
                          </p>
                        </div>
                        <div className="bg-[#F7F8FA] rounded px-2 py-1">
                          <p className="text-[10px] text-[#8A94A6] uppercase">Übernahme neues Auto</p>
                          <p className="font-mono font-bold text-[#111418]">
                            {s.to_mileage_start?.toLocaleString('de-DE') ?? '—'} km
                          </p>
                        </div>
                        <div className={`rounded px-2 py-1 ${s.included_km_exceeded ? 'bg-red-50' : 'bg-emerald-50'}`}>
                          <p className="text-[10px] text-[#8A94A6] uppercase">Gesamt / Inkl.</p>
                          <p className={`font-mono font-bold ${s.included_km_exceeded ? 'text-red-700' : 'text-emerald-700'}`}>
                            {(s.total_driven_km_after_swap ?? 0).toLocaleString('de-DE')} /{' '}
                            {s.included_km_total != null ? s.included_km_total.toLocaleString('de-DE') : '∞'} km
                          </p>
                        </div>
                      </div>
                    )}
                    {s.notes && (
                      <p className="text-xs text-[#111418] mt-1 bg-[#F7F8FA] rounded-sm px-2 py-1">
                        {s.notes}
                      </p>
                    )}
                    {Array.isArray(s.photos) && s.photos.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {s.photos.slice(0, 8).map(p => (
                          <a
                            key={p.photo_id}
                            href={`${API_URL}/api/bookings/${booking.id}/swaps/${s.id}/photos/${p.photo_id}/raw`}
                            target="_blank" rel="noopener noreferrer"
                            title={`${p.phase === 'old_return' ? 'ALT' : 'NEU'} · ${p.slot_label}`}
                          >
                            <img
                              src={`${API_URL}/api/bookings/${booking.id}/swaps/${s.id}/photos/${p.photo_id}/raw`}
                              alt={p.slot_label}
                              className="w-14 h-14 object-cover rounded border border-[#E2E4E9] hover:border-[#FFB800]"
                            />
                          </a>
                        ))}
                        {s.photos.length > 8 && (
                          <span className="text-xs text-[#8A94A6] self-end">+ {s.photos.length - 8}</span>
                        )}
                      </div>
                    )}
                    <div className="mt-2">
                      <a
                        href={`${API_URL}/api/bookings/${booking.id}/swaps/${s.id}/contract-pdf`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#E60000] hover:underline"
                        data-testid={`swap-pdf-${idx}`}
                      >
                        <FileText size={12} weight="bold"/> Vertrags-Nachtrag als PDF
                      </a>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Kilometer-Übersicht (nach Check-Out) */}
          {booking.check_in && booking.check_out && (() => {
            // Berechne über alle Legs (Swaps + aktuelles/letztes Fahrzeug)
            const swaps = Array.isArray(booking.vehicle_swaps) ? booking.vehicle_swaps : [];
            const swapTotal = swaps.reduce((sum, s) => sum + Number(s.from_driven_km || 0), 0);
            // Startkm des letzten Legs
            const lastLegStart = swaps.length > 0
              ? Number(swaps[swaps.length - 1].to_mileage_start || 0)
              : Number(booking.check_in.mileage_start || 0);
            const currentLegDriven = Math.max(0, parseInt(booking.check_out.mileage_end) - lastLegStart);
            const driven = swapTotal + currentLegDriven;
            const included = booking.included_km;
            const rate = booking.extra_km_rate;
            const excess = (included !== null && included !== undefined) ? Math.max(0, driven - parseInt(included)) : 0;
            const charge = excess > 0 && rate ? (excess * parseFloat(rate)) : 0;
            const hasSwaps = swaps.length > 0;
            return (
              <div className="card-swiss p-4" data-testid="km-usage-card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-[#111418]">
                    Kilometer-Übersicht {hasSwaps && <span className="text-xs text-[#8A94A6] font-normal">· {swaps.length + 1} Fahrzeuge</span>}
                  </h3>
                  {excess > 0 && <span className="badge badge-warning">Mehrkilometer</span>}
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="overline text-[#8A94A6]">Gefahren (gesamt)</p>
                    <p className="font-mono font-semibold">{driven.toLocaleString('de-DE')} km</p>
                    {hasSwaps && (
                      <p className="text-[10px] text-[#8A94A6] mt-0.5">
                        Swaps: {swapTotal.toLocaleString('de-DE')} + letztes: {currentLegDriven.toLocaleString('de-DE')}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="overline text-[#8A94A6]">Inklusive</p>
                    <p className="font-mono">{(included !== null && included !== undefined) ? `${parseInt(included).toLocaleString('de-DE')} km` : '∞'}</p>
                  </div>
                  <div>
                    <p className="overline text-[#8A94A6]">Mehr-km</p>
                    <p className={`font-mono font-semibold ${excess > 0 ? 'text-[#E60000]' : 'text-[#009951]'}`}>{excess.toLocaleString('de-DE')} km</p>
                  </div>
                </div>
                {hasSwaps && (
                  <div className="mt-2 pt-2 border-t border-[#E2E4E9]">
                    <p className="text-[11px] text-[#8A94A6] mb-1">Aufschlüsselung nach Fahrzeug:</p>
                    <div className="space-y-0.5 text-[11px] font-mono text-[#525866]">
                      {swaps.map((s, i) => (
                        <div key={i} className="flex justify-between">
                          <span className="truncate mr-2">#{i + 1} {s.from_vehicle_label}</span>
                          <span>{Number(s.from_driven_km || 0).toLocaleString('de-DE')} km</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-semibold text-[#111418]">
                        <span className="truncate mr-2">
                          #{swaps.length + 1} Letztes Fahrzeug (bis Rückgabe)
                        </span>
                        <span>{currentLegDriven.toLocaleString('de-DE')} km</span>
                      </div>
                    </div>
                  </div>
                )}
                {excess > 0 && rate && (
                  <>
                    <div className="mt-3 p-3 bg-[#FFF5F5] border border-[#FFD4D4] rounded-sm flex items-center justify-between">
                      <div>
                        <p className="text-xs text-[#525866]">Nachberechnung ({excess.toLocaleString('de-DE')} km × {parseFloat(rate).toFixed(2)} €)</p>
                        <p className="text-lg font-bold text-[#E60000]">€ {charge.toFixed(2)}</p>
                      </div>
                      <Button
                        size="sm"
                        className="btn-primary"
                        onClick={async () => {
                          if (!window.confirm(`Mehrkilometer-Nachberechnung über € ${charge.toFixed(2)} zur Buchungsrechnung hinzufügen / neue Rechnung erstellen?`)) return;
                          try {
                            const res = await bookingApi.createExtraKmCharge(booking.id);
                            toast.success(res.data.action === 'appended' ? 'Position zur bestehenden Rechnung hinzugefügt' : 'Neue Nachberechnungs-Rechnung erstellt');
                            await loadBooking();
                          } catch (err) {
                            toast.error(formatApiError(err) || 'Nachberechnung fehlgeschlagen');
                          }
                        }}
                        data-testid="create-extra-km-charge-btn"
                      >
                        <CurrencyEur weight="bold" size={14} className="mr-1" />
                        Nachberechnen
                      </Button>
                    </div>
                  </>
                )}
                {excess > 0 && !rate && (
                  <p className="mt-3 text-xs text-[#8A94A6]">Kein €/km-Tarif hinterlegt. Buchung bearbeiten, um Tarif zu setzen.</p>
                )}
              </div>
            );
          })()}

          {/* Reinigungs-Checkliste (nach Rückgabe) */}
          {booking.check_out && booking.vehicle_id && (
            <CleaningPanel vehicleId={booking.vehicle_id} onChanged={loadBooking} />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Price Summary */}
          <div className="card-swiss p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#111418]">Preisübersicht</h3>
              {booking.price_info?.manual_override && (
                <span className="badge badge-warning">Manuell</span>
              )}
            </div>
            <div className="space-y-2 text-sm">
              {(() => {
                const invoiceTotal = bookingInvoices.reduce(
                  (sum, inv) => sum + Number(inv.total || 0),
                  0
                );

                const hasInvoices = bookingInvoices.length > 0;
                const displayTotal = hasInvoices
                  ? invoiceTotal
                  : Number(booking.price_info?.total_price || 0);

                return (
                  <>
                    <div className="flex justify-between">
                      <span>{hasInvoices ? 'Gesamte Rechnungen' : `Grundpreis (${booking.price_info?.days} Tage)`}</span>
                      <span>€{displayTotal.toFixed(2)}</span>
                    </div>

                    {!hasInvoices && booking.price_info?.extras_price > 0 && (
                      <div className="flex justify-between">
                        <span>Extras</span>
                        <span>€{Number(booking.price_info.extras_price).toFixed(2)}</span>
                      </div>
                    )}

                    <div className="flex justify-between font-bold text-base pt-2 border-t border-[#E2E4E9]">
                      <span>Gesamt</span>
                      <span className="text-[#E60000]">€{displayTotal.toFixed(2)}</span>
                    </div>

                    {!hasInvoices && booking.price_info?.manual_override && booking.price_info?.manual_price_note && (
                      <p className="text-xs text-[#8A94A6] pt-1 italic">
                        {booking.price_info.manual_price_note}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Invoice */}
          <div className="card-swiss p-4">
            <h3 id="booking-invoices" className="font-bold text-[#111418] mb-4">Rechnungen</h3>

            {bookingInvoices.length > 0 ? (
              <div className="space-y-4">
                {bookingInvoices.map((inv) => {
                  const openAmount = Number(inv.open_amount ?? (Number(inv.total || 0) - Number(inv.paid_amount || 0)));
                  const depositPaid = Number(inv.deposit_paid_amount ?? (inv.payments || []).filter(payment => payment.deposit_id && payment.method === 'kaution_einbehalt').reduce((sum, payment) => sum + Number(payment.amount || 0), 0));
                  const isExtraKm = (inv.line_items || []).some(item =>
                    String(item.description || '').startsWith('Mehrkilometer')
                  );
                  const isRental = (inv.line_items || []).some(item =>
                    String(item.description || '').startsWith('Fahrzeugmiete:')
                  );

                  return (
                    <div
                      key={inv.id}
                      data-testid={`booking-invoice-${inv.id}`}
                      className="p-3 border border-[#E2E4E9] rounded-sm space-y-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="font-semibold text-[#111418]">
                            {isRental
                              ? 'Mietrechnung'
                              : isExtraKm
                                ? 'Mehrkilometerrechnung'
                                : inv.invoice_type === 'damage' ? 'Schadensrechnung' : 'Rechnung'}
                          </div>
                          <div className="text-xs text-[#8A94A6]">
                            {inv.invoice_number}
                          </div>
                        </div>

                        <span className="font-semibold">
                          €{Number(inv.total || 0).toFixed(2)}
                        </span>
                      </div>

                      <div className="flex justify-between text-sm">
                        <span>Bezahlt</span>
                        <span className="font-medium text-[#009951]">
                          €{Number(inv.paid_amount || 0).toFixed(2)}
                        </span>
                      </div>

                      {depositPaid > 0 && <p className="text-xs font-medium text-emerald-700" data-testid={`booking-invoice-deposit-${inv.id}`}>{euro(depositPaid)} aus Kaution verrechnet</p>}

                      <div className="flex justify-between text-sm font-bold">
                        <span>Offen</span>
                        <span className={openAmount > 0 ? 'text-[#E60000]' : 'text-[#009951]'}>
                          €{openAmount.toFixed(2)}
                        </span>
                      </div>

                      <div className="pt-2 space-y-2">
                        <Button variant="outline" className="w-full" onClick={() => setPreviewInvoice(inv)} data-testid={`preview-invoice-${inv.id}`}>Rechnung ansehen</Button>
                        <Button
                          onClick={() => downloadInvoice(inv)}
                          variant="outline"
                          className="w-full"
                        >
                          <Receipt weight="bold" size={16} className="mr-2" />
                          PDF herunterladen
                        </Button>

                        {openAmount > 0 && (
                          <>
                            <Button
                              onClick={() => {
                                setPaymentInvoice(inv);
                                setPaymentData({
                                  idempotency_key: crypto.randomUUID(),
                                  amount: openAmount.toFixed(2),
                                  method: 'bar',
                                  reference: '',
                                  notes: ''
                                });
                                setShowPaymentModal(true);
                              }}
                              variant="outline"
                              className="w-full"
                            >
                              Manuelle Zahlung
                            </Button>

                            <Button
                              onClick={() => setDepositPaymentInvoice(inv)}
                              variant="outline"
                              className="w-full"
                              data-testid={`pay-invoice-deposit-${inv.id}`}
                            >
                              Mit Kaution bezahlen
                            </Button>

                            {isRental && onlinePaymentsEnabled && (
                              <Button
                                onClick={handleStripePayment}
                                className="btn-primary w-full"
                                data-testid="stripe-pay-btn"
                              >
                                <CreditCard weight="bold" size={16} className="mr-2" />
                                Online bezahlen
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Button
                onClick={createInvoice}
                className="w-full btn-secondary"
                data-testid="create-invoice-btn"
                disabled={creatingInvoice || !invoicesLoaded}
                aria-busy={creatingInvoice}
              >
                {creatingInvoice ? 'Rechnung wird erstellt …' : !invoicesLoaded ? 'Rechnungen werden geladen …' : 'Rechnung erstellen'}
              </Button>
            )}
          </div>

          {/* Kautionen */}
          <div className="card-swiss p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 id="booking-deposits" className="font-bold text-[#111418]">Kautionen</h3>
              <Button
                onClick={openDepositModal}
                size="sm"
                className="btn-primary"
                data-testid="add-deposit-btn"
              >
                <Money weight="bold" size={14} className="mr-1" />
                Kaution hinzufügen
              </Button>
            </div>
            {deposits.length === 0 ? (
              <p className="text-sm text-[#8A94A6]">Keine Kautionen erfasst</p>
            ) : (
              <div className="space-y-2">
                {deposits.map(dep => {
                  const status = dep.status === 'zurückgegeben' ? 'released' : (dep.status || 'hinterlegt');
                  const meta = DEPOSIT_STATUS_META[status] || DEPOSIT_STATUS_META.hinterlegt;
                  const isSettled = ['pending_refund', 'released', 'partial_retained', 'fully_retained'].includes(status);
                  return (
                    <div
                      key={dep.id}
                      className="p-3 border border-[#E2E4E9] rounded-sm space-y-2"
                      data-testid={`booking-deposit-${dep.id}`}
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[#111418]">€ {Number(dep.amount).toFixed(2)}</span>
                            <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded border ${meta.badge}`}>
                              {meta.label}
                            </span>
                            <span className="text-xs uppercase text-[#8A94A6]">{dep.payment_method || 'bar'}</span>
                          </div>
                          <p className="text-xs text-[#8A94A6] truncate">{dep.description}</p>
                          {isSettled && (
                            <p className="text-xs text-[#525866] mt-1">
                              Einbehalt: <span className="font-semibold">€ {Number(dep.retained_amount || 0).toFixed(2)}</span>
                              {' · '}Rückzahlung: <span className="font-semibold">€ {Number(dep.refund_amount || 0).toFixed(2)}</span>
                              {dep.retention_invoice_number && (
                                <> · Rechnung <span className="font-semibold">{dep.retention_invoice_number}</span></>
                              )}
                            </p>
                          )}
                        </div>
                        <DepositWorkflowActions deposit={dep} onChanged={loadBooking} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="card-swiss p-4">
            <h3 className="font-bold text-[#111418] mb-4">Dokumente</h3>
            <div className="space-y-2">
              <Button onClick={() => setShowContractPreview(true)} variant="outline" className="w-full" data-testid="preview-contract-btn">Mietvertrag ansehen</Button>
              <Button onClick={downloadContract} variant="outline" className="w-full" data-testid="download-contract-btn">
                <FileText weight="bold" size={16} className="mr-2" />
                Vertrag herunterladen
              </Button>
              <Button onClick={downloadAccidentReport} variant="outline" className="w-full" data-testid="download-accident-report-btn">
                <FileText weight="bold" size={16} className="mr-2" />Unfallbericht herunterladen
              </Button>
              <p className="text-xs text-[#8A94A6]">PDF zum Ausfüllen oder Ausdrucken für den Kunden. Skizze und Unterschriften handschriftlich ergänzen.</p>
              <Button onClick={downloadProtocol} variant="outline" className="w-full" data-testid="download-protocol-btn">
                <FileText weight="bold" size={16} className="mr-2" />
                Übergabeprotokoll herunterladen
              </Button>
              <Button
                onClick={() => navigate(`/damages?booking=${id}`)}
                variant="outline"
                className="w-full"
                data-testid="report-damage-btn"
              >
                <Warning weight="bold" size={16} className="mr-2" />
                Schaden erfassen
              </Button>
              <Button
                onClick={() => navigate(`/letters?new=1&customer_id=${booking.customer_id}&booking_id=${id}`)}
                variant="outline"
                className="w-full"
                data-testid="create-letter-for-booking-btn"
              >
                <EnvelopeSimple weight="bold" size={16} className="mr-2" />
                Brief zu dieser Buchung
              </Button>
              <Button 
                onClick={handleSendConfirmation} 
                variant="outline" 
                className="w-full"
                data-testid="send-confirmation-btn"
              >
                <EnvelopeSimple weight="bold" size={16} className="mr-2" />
                Bestätigung senden
              </Button>

              <Button
                onClick={sendRegistrationDocumentEmail}
                variant="outline"
                className="w-full"
                data-testid="send-registration-document-btn"
              >
                <EnvelopeSimple weight="bold" size={16} className="mr-2" />
                Fahrzeugschein per E-Mail senden
              </Button>
            </div>
          </div>

          {/* Zugehörige Briefe */}
          {relatedLetters.length > 0 && (
            <div className="card-swiss p-4" data-testid="related-letters-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-[#111418]">Briefe zu dieser Buchung</h3>
                <span className="text-xs text-[#8A94A6]">{relatedLetters.length}</span>
              </div>
              <div className="space-y-2">
                {relatedLetters.map((l) => {
                  const statusStyles = {
                    draft: 'bg-[#F7F8FA] text-[#525866] border-[#E5E7EB]',
                    sent: 'bg-[#E6F7EB] text-[#009951] border-[#B7E4C7]',
                    archived: 'bg-[#F0F1F3] text-[#8A94A6] border-[#E5E7EB]',
                  };
                  const statusLabels = { draft: 'Entwurf', sent: 'Gesendet', archived: 'Archiv' };
                  return (
                    <button
                      key={l.id}
                      onClick={() => navigate(`/letters?open=${l.id}`)}
                      className="w-full text-left p-2 rounded-sm border border-[#E5E7EB] hover:border-[#E60000] transition-colors"
                      data-testid={`related-letter-${l.id}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-[#8A94A6]">{l.letter_number}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-sm border ${statusStyles[l.status]}`}>
                          {statusLabels[l.status] || l.status}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-[#111418] mt-1 truncate">{l.subject}</p>
                      <p className="text-xs text-[#525866] mt-0.5">
                        {new Date(l.created_at).toLocaleDateString('de-DE')}
                        {l.email_sent_at && ' · per E-Mail'}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Übergabe Modal */}
      <Dialog open={showCheckInModal} onOpenChange={setShowCheckInModal}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fahrzeug-Übergabe an Mieter</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCheckIn} className="space-y-4">
            <DriverLicenseWarnings booking={booking} />
            <Button type="button" variant="outline" onClick={() => setShowContractPreview(true)}>Mietvertrag vor Unterschrift ansehen</Button>
            <div>
              <Label className="overline">Übergabe-Datum & Uhrzeit</Label>
              <Input
                type="datetime-local"
                value={checkInData.performed_at}
                onChange={(e) => setCheckInData({...checkInData, performed_at: e.target.value})}
                className="input-swiss"
                data-testid="checkin-performed-at-input"
              />
              <p className="text-xs text-[#8A94A6] mt-1">
                Leer lassen für jetzt. Für nachträgliche Erfassung alter Mietverträge → tatsächliches Übergabe-Datum eintragen.
              </p>
            </div>
            <div>
              <Label className="overline">Kilometerstand</Label>
              <Input
                type="number"
                value={checkInData.mileage_start}
                onChange={(e) => setCheckInData({...checkInData, mileage_start: e.target.value})}
                className="input-swiss"
                required
                data-testid="checkin-mileage-input"
              />
              {booking.vehicle?.current_mileage != null && (() => {
                const ref = parseInt(booking.vehicle.current_mileage);
                const entered = parseInt(checkInData.mileage_start);
                const isDiff = !isNaN(entered) && entered !== ref;
                const delta = !isNaN(entered) ? entered - ref : 0;
                return (
                  <div
                    className={`mt-1.5 text-xs rounded-sm px-2 py-1.5 border ${isDiff ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-[#F0F7FF] border-[#C7E0F4] text-[#004578]'}`}
                    data-testid="checkin-km-hint"
                  >
                    {isDiff ? (
                      <>
                        <span className="font-semibold">⚠ Abweichung {delta > 0 ? '+' : ''}{delta.toLocaleString('de-DE')} km</span>
                        {' — '}letzter bekannter Stand: <span className="font-mono">{ref.toLocaleString('de-DE')} km</span> (manuell überschrieben)
                      </>
                    ) : (
                      <>
                        Letzter bekannter Stand: <span className="font-mono font-semibold">{ref.toLocaleString('de-DE')} km</span> — bitte prüfen. Bei Abweichung Wert anpassen.
                      </>
                    )}
                  </div>
                );
              })()}
            </div>

            <div>
              <Label className="overline">Tankstand</Label>
              <Select 
                value={checkInData.fuel_level_start} 
                onValueChange={(v) => setCheckInData({...checkInData, fuel_level_start: v})}
              >
                <SelectTrigger className="input-swiss">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="voll">Voll</SelectItem>
                  <SelectItem value="3/4">3/4</SelectItem>
                  <SelectItem value="1/2">1/2</SelectItem>
                  <SelectItem value="1/4">1/4</SelectItem>
                  <SelectItem value="leer">Leer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Zustandsnotizen</Label>
              <textarea
                value={checkInData.condition_notes}
                onChange={(e) => setCheckInData({...checkInData, condition_notes: e.target.value})}
                className="input-swiss w-full px-3 py-2"
                rows={3}
              />
            </div>
            <div className="border-t border-[#E2E4E9] pt-3">
              <PhotoSlotGrid
                bookingId={id}
                phase="checkin"
                slots={photoSlots || []}
                existingPhotos={livePhotosCheckIn}
                onChange={(newPhotos) => setLivePhotosCheckIn(newPhotos)}
              />
            </div>
            <div className="border-t border-[#E2E4E9] pt-3">
              <Label className="overline mb-2 block">Unterschriften</Label>
              <div className="flex gap-2 mb-2">
                <Button
                  type="button"
                  variant={checkInSigShow === 'customer' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCheckInSigShow(checkInSigShow === 'customer' ? null : 'customer')}
                  className={`${checkInSigCustomer ? 'border-[#009951] text-[#009951]' : ''}`}
                  data-testid="checkin-sig-customer-btn"
                >
                  {checkInSigCustomer ? '✓ ' : ''}Mieter{checkInSigCustomer ? ' (signiert)' : ''}
                </Button>
                <Button
                  type="button"
                  variant={checkInSigShow === 'landlord' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCheckInSigShow(checkInSigShow === 'landlord' ? null : 'landlord')}
                  className={`${checkInSigLandlord ? 'border-[#009951] text-[#009951]' : ''}`}
                  data-testid="checkin-sig-landlord-btn"
                >
                  {checkInSigLandlord ? '✓ ' : ''}Vermieter{checkInSigLandlord ? ' (signiert)' : ''}
                </Button>
              </div>
              {checkInSigShow === 'customer' && (
                <div className="p-2 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
                  <p className="text-xs text-[#8A94A6] mb-1">Unterschrift des Mieters</p>
                  <SignaturePad onSignatureChange={setCheckInSigCustomer} />
                </div>
              )}
              {checkInSigShow === 'landlord' && (
                <div className="p-2 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
                  <p className="text-xs text-[#8A94A6] mb-1">Unterschrift des Vermieters</p>
                  <SignaturePad onSignatureChange={setCheckInSigLandlord} />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCheckInModal(false)}>
                Abbrechen
              </Button>
              <Button
                type="button"
                className="btn-primary"
                data-testid="confirm-checkin-btn"
                onClick={handleCheckIn}
              >
                Übergabe bestätigen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AGB / Datenschutz Bestätigung */}
      <Dialog open={showComplianceModal} onOpenChange={setShowComplianceModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>AGB & Datenschutz</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border border-[#E2E4E9] bg-[#F7F8FA] p-4">
              <p className="font-semibold text-sm mb-2">
                Vor der Fahrzeugübergabe
              </p>
              <p className="text-sm text-[#5F6877]">
                Der Mieter bestätigt, dass er die folgenden Unterlagen gelesen
                und zur Kenntnis genommen hat.
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={checkInData.agb_acknowledged}
                onChange={(e) => setCheckInData(prev => ({
                  ...prev,
                  agb_acknowledged: e.target.checked
                }))}
                className="mt-1 h-4 w-4"
                data-testid="compliance-agb-checkbox"
              />
              <span className="text-sm">
                Ich habe die{' '}
                <button
                  type="button"
                  className="underline font-medium"
                  onClick={() => window.open('/agb', '_blank')}
                >
                  Allgemeinen Geschäftsbedingungen (AGB)
                </button>{' '}
                gelesen und zur Kenntnis genommen.
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={checkInData.privacy_acknowledged}
                onChange={(e) => setCheckInData(prev => ({
                  ...prev,
                  privacy_acknowledged: e.target.checked
                }))}
                className="mt-1 h-4 w-4"
                data-testid="compliance-privacy-checkbox"
              />
              <span className="text-sm">
                Ich habe die{' '}
                <button
                  type="button"
                  className="underline font-medium"
                  onClick={() => window.open('/datenschutz', '_blank')}
                >
                  Datenschutzhinweise
                </button>{' '}
                gelesen und zur Kenntnis genommen.
              </span>
            </label>

            <div className="border-t border-[#E2E4E9] pt-4">
              <Label className="overline mb-2 block">
                Unterschrift des Mieters
              </Label>

              <p className="text-xs text-[#8A94A6] mb-2">
                Mit der Unterschrift bestätigt der Mieter die Kenntnisnahme
                der AGB und Datenschutzhinweise.
              </p>

              <div className="p-2 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
                <SignaturePad
                  onSignatureChange={setComplianceSignature}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowComplianceModal(false)}
            >
              Zurück
            </Button>

            <Button
              type="button"
              className="btn-primary"
              disabled={
                !checkInData.agb_acknowledged ||
                !checkInData.privacy_acknowledged ||
                !complianceSignature
              }
              data-testid="confirm-compliance-btn"
              onClick={async () => {
                try {
                  const acknowledgedAt = new Date().toISOString();

                  const finalCheckInData = {
                    ...checkInData,
                    mileage_start: parseInt(checkInData.mileage_start),
                    signature_customer: checkInSigCustomer,
                    signature_landlord: checkInSigLandlord,
                    compliance_signature: complianceSignature,
                    acknowledged_at: acknowledgedAt,
                  };

                  await bookingApi.checkIn(id, finalCheckInData);

                  toast.success('Übergabe erfolgreich');
                  setShowComplianceModal(false);
                  setShowCheckInModal(false);
                  setCheckInPhotos([]);
                  setCheckInSigCustomer(null);
                  setCheckInSigLandlord(null);
                  setCheckInSigShow(null);
                  setComplianceSignature(null);
                  loadBooking();
                } catch (error) {
                  toast.error(formatApiError(error));
                }
              }}
            >
              Bestätigen & Fahrzeug übergeben
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rückgabe Modal */}
      <Dialog open={showCheckOutModal} onOpenChange={setShowCheckOutModal}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fahrzeug-Rückgabe vom Mieter</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCheckOut} className="space-y-4">
            <div>
              <Label className="overline">Rückgabe-Datum & Uhrzeit</Label>
              <Input
                type="datetime-local"
                value={checkOutData.performed_at}
                onChange={(e) => setCheckOutData({...checkOutData, performed_at: e.target.value})}
                className="input-swiss"
                data-testid="checkout-performed-at-input"
              />
              <p className="text-xs text-[#8A94A6] mt-1">
                Leer lassen für jetzt. Für nachträgliche Erfassung alter Mietverträge → tatsächliches Rückgabe-Datum eintragen.
              </p>
            </div>
            <div>
              <Label className="overline">Kilometerstand</Label>
              <Input
                type="number"
                value={checkOutData.mileage_end}
                onChange={(e) => setCheckOutData({...checkOutData, mileage_end: e.target.value})}
                className="input-swiss"
                required
                min={booking.check_in?.mileage_start || 0}
                data-testid="checkout-mileage-input"
              />
              {/* Live kilometer breakdown */}
              {checkOutData.mileage_end && booking.check_in?.mileage_start !== undefined && (() => {
                const driven = Math.max(0, parseInt(checkOutData.mileage_end) - parseInt(booking.check_in.mileage_start || 0));
                const included = booking.included_km;
                const rate = booking.extra_km_rate;
                const excess = (included !== null && included !== undefined) ? Math.max(0, driven - parseInt(included)) : 0;
                const charge = excess > 0 && rate ? (excess * parseFloat(rate)) : 0;
                return (
                  <div className="mt-2 p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm text-sm" data-testid="checkout-km-breakdown">
                    <div className="flex items-center justify-between">
                      <span className="text-[#525866]">Gefahren:</span>
                      <span className="font-mono font-semibold text-[#111418]">{driven.toLocaleString('de-DE')} km</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#525866]">Inklusivkilometer:</span>
                      <span className="font-mono text-[#111418]">{included !== null && included !== undefined ? `${parseInt(included).toLocaleString('de-DE')} km` : 'Unbegrenzt'}</span>
                    </div>
                    {excess > 0 && (
                      <>
                        <div className="flex items-center justify-between text-[#E60000] font-medium mt-1">
                          <span>Mehrkilometer:</span>
                          <span className="font-mono">{excess.toLocaleString('de-DE')} km</span>
                        </div>
                        {rate && !checkOutData.km_kulanz && (
                          <div className="flex items-center justify-between text-[#E60000] mt-1 pt-1 border-t border-[#E2E4E9]">
                            <span>Nachberechnung ({rate} €/km):</span>
                            <span className="font-mono font-bold">€ {charge.toFixed(2)}</span>
                          </div>
                        )}
                        {rate && checkOutData.km_kulanz && (
                          <div className="flex items-center justify-between text-[#16803C] mt-1 pt-1 border-t border-[#E2E4E9]">
                            <span>Kulanz – Mehrkilometer nicht berechnet:</span>
                            <span className="font-mono font-bold">€ 0,00</span>
                          </div>
                        )}
                        {!rate && !checkOutData.km_kulanz && (
                          <p className="text-xs text-[#8A94A6] mt-1">⚠ Kein €/km-Tarif gesetzt – Buchung bearbeiten, um zu hinterlegen.</p>
                        )}

                        {/* KM-Kulanz Checkbox */}
                        <label className="flex items-start gap-3 mt-3 p-3 border border-[#E2E4E9] rounded-sm cursor-pointer hover:bg-white">
                          <input
                            type="checkbox"
                            checked={checkOutData.km_kulanz}
                            onChange={(e) => setCheckOutData({
                              ...checkOutData,
                              km_kulanz: e.target.checked
                            })}
                            className="mt-1 h-4 w-4"
                            data-testid="checkout-km-kulanz-checkbox"
                          />
                          <div>
                            <div className="font-medium text-[#111418]">
                              Mehrkilometer aus Kulanz nicht berechnen
                            </div>
                            <div className="text-xs text-[#8A94A6] mt-1">
                              Die gefahrenen Mehrkilometer werden dokumentiert,
                              aber dem Kunden nicht berechnet.
                            </div>
                          </div>
                        </label>

                        {checkOutData.km_kulanz && (
                          <div className="mt-2 text-xs font-medium text-[#16803C]">
                            ✓ Mehrkilometer werden aus Kulanz nicht berechnet.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            <div>
              <Label className="overline">Tankstand</Label>
              <Select 
                value={checkOutData.fuel_level_end} 
                onValueChange={(v) => setCheckOutData({...checkOutData, fuel_level_end: v})}
              >
                <SelectTrigger className="input-swiss">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="voll">Voll</SelectItem>
                  <SelectItem value="3/4">3/4</SelectItem>
                  <SelectItem value="1/2">1/2</SelectItem>
                  <SelectItem value="1/4">1/4</SelectItem>
                  <SelectItem value="leer">Leer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Zustandsnotizen</Label>
              <textarea
                value={checkOutData.condition_notes}
                onChange={(e) => setCheckOutData({...checkOutData, condition_notes: e.target.value})}
                className="input-swiss w-full px-3 py-2"
                rows={2}
              />
            </div>
            <div>
              <Label className="overline">Schäden (falls vorhanden)</Label>
              <textarea
                value={checkOutData.damage_notes}
                onChange={(e) => setCheckOutData({...checkOutData, damage_notes: e.target.value})}
                className="input-swiss w-full px-3 py-2"
                rows={2}
              />
            </div>
            <div>
              <Label className="overline">Fotos (optional)</Label>
              <PhotoSlotGrid
                bookingId={id}
                phase="checkout"
                slots={photoSlots || []}
                existingPhotos={livePhotosCheckOut}
                onChange={(newPhotos) => setLivePhotosCheckOut(newPhotos)}
              />
            </div>
            <div className="border-t border-[#E2E4E9] pt-3">
              <Label className="overline mb-2 block">Unterschriften</Label>
              <div className="flex gap-2 mb-2">
                <Button
                  type="button"
                  variant={checkOutSigShow === 'customer' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCheckOutSigShow(checkOutSigShow === 'customer' ? null : 'customer')}
                  className={`${checkOutSigCustomer ? 'border-[#009951] text-[#009951]' : ''}`}
                  data-testid="checkout-sig-customer-btn"
                >
                  {checkOutSigCustomer ? '✓ ' : ''}Mieter{checkOutSigCustomer ? ' (signiert)' : ''}
                </Button>
                <Button
                  type="button"
                  variant={checkOutSigShow === 'landlord' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCheckOutSigShow(checkOutSigShow === 'landlord' ? null : 'landlord')}
                  className={`${checkOutSigLandlord ? 'border-[#009951] text-[#009951]' : ''}`}
                  data-testid="checkout-sig-landlord-btn"
                >
                  {checkOutSigLandlord ? '✓ ' : ''}Vermieter{checkOutSigLandlord ? ' (signiert)' : ''}
                </Button>
              </div>
              {checkOutSigShow === 'customer' && (
                <div className="p-2 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
                  <p className="text-xs text-[#8A94A6] mb-1">Unterschrift des Mieters</p>
                  <SignaturePad onSignatureChange={setCheckOutSigCustomer} />
                </div>
              )}
              {checkOutSigShow === 'landlord' && (
                <div className="p-2 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
                  <p className="text-xs text-[#8A94A6] mb-1">Unterschrift des Vermieters</p>
                  <SignaturePad onSignatureChange={setCheckOutSigLandlord} />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCheckOutModal(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="btn-secondary" data-testid="confirm-checkout-btn">
                Rückgabe bestätigen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <DepositInvoicePayment invoice={depositPaymentInvoice} onClose={() => setDepositPaymentInvoice(null)} onChanged={handleDepositPaymentRecorded} />

      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manuelle Zahlung erfassen</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleManualPayment} className="space-y-4">
            <div>
              <Label className="overline">Betrag (€)</Label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, idempotency_key: crypto.randomUUID(), amount: e.target.value})}
                className="input-swiss"
                step="0.01"
                max={invoice?.total - invoice?.paid_amount}
                required
              />
            </div>
            <div>
              <Label className="overline">Zahlungsart</Label>
              <Select 
                value={paymentData.method} 
                onValueChange={(v) => setPaymentData({...paymentData, idempotency_key: crypto.randomUUID(), method: v})}
              >
                <SelectTrigger className="input-swiss">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="ec">EC-Karte</SelectItem>
                  <SelectItem value="überweisung">Überweisung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Referenz (optional)</Label>
              <Input
                value={paymentData.reference}
                onChange={(e) => setPaymentData({...paymentData, idempotency_key: crypto.randomUUID(), reference: e.target.value})}
                className="input-swiss"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="btn-primary">
                Zahlung erfassen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deposit Modal */}
      <Dialog open={showDepositModal} onOpenChange={setShowDepositModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Kaution hinterlegen</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitDeposit} className="space-y-4">
            <div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm text-sm">
              <div className="flex justify-between">
                <span className="text-[#8A94A6]">Kunde</span>
                <span className="font-medium">
                  {booking?.customer ? `${booking.customer.first_name} ${booking.customer.last_name}` : '—'}
                </span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[#8A94A6]">Buchung</span>
                <span className="font-medium">{booking?.booking_number || id?.substring(0, 8)}</span>
              </div>
            </div>
            <div>
              <Label className="overline">Betrag (€)</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={depositForm.amount}
                onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
                className="input-swiss"
                required
                data-testid="booking-deposit-amount"
              />
            </div>
            <div>
              <Label className="overline">Zahlungsart</Label>
              <Select
                value={depositForm.payment_method}
                onValueChange={(v) => setDepositForm({ ...depositForm, payment_method: v })}
              >
                <SelectTrigger className="input-swiss" data-testid="booking-deposit-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar (ins Kassenbuch)</SelectItem>
                  <SelectItem value="ec">EC / Karte</SelectItem>
                  <SelectItem value="ueberweisung">Überweisung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Beschreibung</Label>
              <Input
                value={depositForm.description}
                onChange={(e) => setDepositForm({ ...depositForm, description: e.target.value })}
                className="input-swiss"
              />
            </div>
            <div>
              <Label className="overline">Notizen</Label>
              <textarea
                value={depositForm.notes}
                onChange={(e) => setDepositForm({ ...depositForm, notes: e.target.value })}
                className="input-swiss w-full px-3 py-2"
                rows={2}
              />
            </div>
            <div>
              <Label className="overline mb-2 block">Unterschrift des Kunden (optional)</Label>
              <SignaturePad onSignatureChange={setDepositSignature} />
              <p className="text-xs text-[#8A94A6] mt-1">Eine Quittung (PDF) wird nach dem Speichern automatisch generiert & heruntergeladen.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDepositModal(false)}>
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="btn-primary"
                disabled={!depositForm.amount}
                data-testid="submit-booking-deposit-btn"
              >
                Kaution hinterlegen
              </Button>
              {onlinePaymentsEnabled && booking?.deposit_amount > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await paymentApi.checkoutDeposit(id);
                      if (res.data?.url) { window.open(res.data.url, '_blank'); toast.success('Stripe-Bezahlseite geöffnet'); }
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  data-testid="stripe-deposit-btn"
                >
                  💳 Online via Stripe
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Modal */}
      <Dialog open={showEditBooking} onOpenChange={setShowEditBooking}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Buchung bearbeiten</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEditBooking} className="space-y-4">
            <div>
              <Label className="overline">Kunde (aus Stammdaten wählen)</Label>
              <Select
                value={editBookingForm.customer_id}
                onValueChange={(v) => setEditBookingForm({ ...editBookingForm, customer_id: v })}
              >
                <SelectTrigger className="input-swiss" data-testid="edit-booking-customer-select">
                  <SelectValue placeholder="Kunde wählen" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.first_name} {c.last_name}{c.email ? ` — ${c.email}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8A94A6] mt-1">
                Wechselt den zugeordneten Kundenstamm. Für manuelle Anpassung (z.B. abweichende Rechnungsadresse) nutze die Felder unten.
              </p>
            </div>

            <CustomerOverrideFields
              customer={
                customers.find((c) => c.id === editBookingForm.customer_id) || booking?.customer
              }
              override={editBookingForm.customer_override}
              onChange={(ov) => setEditBookingForm({ ...editBookingForm, customer_override: ov })}
            />

            <div>
              <Label className="overline">Fahrzeug</Label>
              <Select
                value={editBookingForm.vehicle_id}
                onValueChange={(v) => setEditBookingForm({ ...editBookingForm, vehicle_id: v })}
              >
                <SelectTrigger className="input-swiss"><SelectValue placeholder="Fahrzeug wählen" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.license_plate}) — €{v.daily_rate}/Tag</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Von</Label>
                <Input type="date" value={editBookingForm.start_date} onChange={e => setEditBookingForm({ ...editBookingForm, start_date: e.target.value })} className="input-swiss" />
              </div>
              <div>
                <Label className="overline">Bis</Label>
                <Input type="date" value={editBookingForm.end_date} onChange={e => setEditBookingForm({ ...editBookingForm, end_date: e.target.value })} className="input-swiss" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Abholort</Label>
                <Input value={editBookingForm.pickup_location} onChange={e => setEditBookingForm({ ...editBookingForm, pickup_location: e.target.value })} className="input-swiss" />
              </div>
              <div>
                <Label className="overline">Rückgabeort</Label>
                <Input value={editBookingForm.return_location} onChange={e => setEditBookingForm({ ...editBookingForm, return_location: e.target.value })} className="input-swiss" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="overline">Km-Stand</Label>
                <Input type="number" value={editBookingForm.pickup_mileage} onChange={e => setEditBookingForm({ ...editBookingForm, pickup_mileage: e.target.value })} className="input-swiss" />
              </div>
              <div>
                <Label className="overline">Kaution (€)</Label>
                <Input type="number" step="0.01" value={editBookingForm.deposit_amount} onChange={e => setEditBookingForm({ ...editBookingForm, deposit_amount: e.target.value })} className="input-swiss" />
              </div>
              <div>
                <Label className="overline">Selbstbehalt (€)</Label>
                <Input type="number" step="0.01" value={editBookingForm.insurance_deductible} onChange={e => setEditBookingForm({ ...editBookingForm, insurance_deductible: e.target.value })} className="input-swiss" />
              </div>
            </div>
            <div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
              <Label className="overline mb-2 block">Kilometer-Pauschale</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#525866]">Inklusivkilometer (gesamt)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    value={editBookingForm.included_km}
                    onChange={e => setEditBookingForm({ ...editBookingForm, included_km: e.target.value })}
                    placeholder="z. B. 1500 (leer = unbegrenzt)"
                    className="input-swiss"
                    data-testid="edit-booking-included-km"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#525866]">€ je Mehrkilometer</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={editBookingForm.extra_km_rate}
                    onChange={e => setEditBookingForm({ ...editBookingForm, extra_km_rate: e.target.value })}
                    placeholder="z. B. 0.30"
                    className="input-swiss"
                    data-testid="edit-booking-extra-km-rate"
                  />
                </div>
              </div>
              <p className="text-xs text-[#8A94A6] mt-2">
                Vorbefüllt aus Fahrzeug-Stammdaten. Mehrkilometer werden beim Check-Out automatisch berechnet.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editBookingForm.use_weekend_rate}
                onChange={e => setEditBookingForm({ ...editBookingForm, use_weekend_rate: e.target.checked })}
              />
              Wochenendtarif verwenden
            </label>
            <div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm space-y-3">
              <div className="flex items-center justify-between">
                <Label className="overline">Preis manuell anpassen (optional)</Label>
                {booking?.price_info?.manual_override && (
                  <Button type="button" variant="outline" size="sm" onClick={resetManualPrice} className="text-[#E60000] border-[#E60000]">
                    Zurücksetzen (Auto)
                  </Button>
                )}
              </div>
              <p className="text-xs text-[#8A94A6]">
                Aktueller Preis: <strong>€ {Number(booking?.price_info?.total_price || 0).toFixed(2)}</strong>
                {booking?.price_info?.manual_override ? ' (manuell)' : ' (automatisch)'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#8A94A6]">Tagespreis (€)</Label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={editBookingForm.manual_daily_rate}
                    onChange={(e) => setEditBookingForm({ ...editBookingForm, manual_daily_rate: e.target.value })}
                    placeholder="leer = auto"
                    className="input-swiss"
                    data-testid="manual-daily-rate"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8A94A6]">Grundpreis gesamt (€)</Label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={editBookingForm.manual_base_price}
                    onChange={(e) => setEditBookingForm({ ...editBookingForm, manual_base_price: e.target.value })}
                    placeholder="leer = auto"
                    className="input-swiss"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8A94A6]">Extras-Preis (€)</Label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={editBookingForm.manual_extras_price}
                    onChange={(e) => setEditBookingForm({ ...editBookingForm, manual_extras_price: e.target.value })}
                    placeholder="leer = auto"
                    className="input-swiss"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8A94A6]">Gesamtpreis Brutto (€)</Label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={editBookingForm.manual_total_price}
                    onChange={(e) => setEditBookingForm({ ...editBookingForm, manual_total_price: e.target.value })}
                    placeholder="leer = auto"
                    className="input-swiss font-semibold"
                    data-testid="manual-total-price"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#8A94A6]">Begründung/Notiz zum Sonderpreis</Label>
                <Input
                  value={editBookingForm.manual_price_note}
                  onChange={(e) => setEditBookingForm({ ...editBookingForm, manual_price_note: e.target.value })}
                  placeholder="z.B. Stammkundenrabatt, Sonderaktion"
                  className="input-swiss"
                />
              </div>
              <p className="text-xs text-[#8A94A6]">
                Hinweis: „Gesamtpreis" überschreibt alle anderen Werte. Leer lassen = automatische Berechnung.
              </p>
            </div>
            <AdditionalDriversFields customers={customers} mainCustomerId={editBookingForm.customer_id} value={editBookingForm.additional_drivers || []} onChange={(drivers) => setEditBookingForm({ ...editBookingForm, additional_drivers: drivers })} />

            <div>
              <Label className="overline">Notizen</Label>
              <textarea
                value={editBookingForm.notes}
                onChange={e => setEditBookingForm({ ...editBookingForm, notes: e.target.value })}
                className="input-swiss w-full px-3 py-2"
                rows={2}
              />
            </div>
            <BookingChangePreview booking={booking} form={editBookingForm} onPreview={setChangePreview} />
            <p className="text-xs text-[#8A94A6]">Bei Änderung von Fahrzeug, Datum oder Tarif wird der Preis automatisch neu berechnet (es sei denn, ein manueller Preis wurde gesetzt).</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditBooking(false)}>Abbrechen</Button>
              <Button type="submit" className="btn-primary" disabled={savingBooking || (editBookingForm.end_date !== booking.end_date?.slice(0, 10) && changePreview?.key !== JSON.stringify(bookingEditPayload(editBookingForm)))} data-testid="submit-edit-booking-btn">{savingBooking ? 'Wird gespeichert …' : 'Speichern'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <RevisionHistory
        open={showHistory}
        onOpenChange={setShowHistory}
        entityType="booking"
        entityId={id}
        title={`Buchung #${booking?.id?.substring(0, 8).toUpperCase()}`}
      />

      <VehicleSwapDialog
        booking={booking}
        currentVehicle={booking?.vehicle}
        open={showSwapDialog}
        onOpenChange={setShowSwapDialog}
        onSwapped={async () => { await loadBooking(); }}
      />

      {/* Fristlose Kündigung Dialog */}
      <Dialog open={showTerminate} onOpenChange={setShowTerminate}>
        <DialogContent className="max-w-md" data-testid="terminate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#E60000]">
              <Prohibit weight="bold" size={20} />
              Vertrag fristlos kündigen
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTerminate} className="space-y-4">
            <div className="p-3 bg-[#FFF5F5] border border-[#FFD4D4] rounded-sm text-sm text-[#111418]">
              <Warning weight="bold" size={16} className="inline mr-1 text-[#E60000]" />
              Der Mietvertrag wird <strong>sofort beendet</strong> und das Fahrzeug wieder freigegeben.
              Diese Aktion sollte nur bei schwerwiegenden Verstößen erfolgen.
            </div>

            <div>
              <Label className="overline">Kündigungsgrund *</Label>
              <Select value={termReason} onValueChange={setTermReason}>
                <SelectTrigger className="mt-1" data-testid="terminate-reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TERMINATION_REASON_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`terminate-reason-${opt.value}`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="overline">End-Kilometerstand (optional)</Label>
              <Input
                type="number"
                min="0"
                value={termMileage}
                onChange={(e) => setTermMileage(e.target.value)}
                placeholder="z.B. 45320"
                className="mt-1 font-mono"
                data-testid="terminate-mileage-input"
              />
            </div>

            <div>
              <Label className="overline">Notizen / Begründung</Label>
              <textarea
                className="mt-1 w-full min-h-[90px] border border-[#E5E7EB] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E60000]"
                value={termNotes}
                onChange={(e) => setTermNotes(e.target.value)}
                placeholder="Kurze Beschreibung des Vorfalls / Verstoßes …"
                data-testid="terminate-notes-input"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowTerminate(false)}
                disabled={terminating}
                data-testid="terminate-cancel-btn"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={terminating}
                className="bg-[#E60000] hover:bg-[#B80000] text-white"
                data-testid="terminate-submit-btn"
              >
                {terminating ? 'Wird gekündigt…' : 'Jetzt fristlos kündigen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Folge-Dialog: E-Mail-Versand der Kündigungsbestätigung */}
      <Dialog open={showTerminateEmail} onOpenChange={setShowTerminateEmail}>
        <DialogContent className="max-w-md" data-testid="terminate-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EnvelopeSimple weight="bold" size={20} className="text-[#E60000]" />
              Kündigungsbestätigung versenden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-[#F7F8FA] border border-[#E5E7EB] rounded-sm text-sm">
              <p className="text-[#111418]">
                Die Kündigungs-PDF wurde erstellt. Soll sie jetzt per E-Mail an den Kunden gesendet werden?
              </p>
              {booking?.customer?.email ? (
                <p className="mt-2 text-xs text-[#525866]">
                  Empfänger: <span className="font-semibold text-[#111418]" data-testid="terminate-email-recipient">{booking.customer.email}</span>
                </p>
              ) : (
                <p className="mt-2 text-xs text-[#E60000] font-semibold">
                  Für diesen Kunden ist keine E-Mail-Adresse hinterlegt. Bitte PDF herunterladen und manuell zustellen.
                </p>
              )}
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-col sm:gap-2">
              {booking?.customer?.email && (
                <Button
                  type="button"
                  onClick={sendTerminationEmail}
                  disabled={sendingTerminateEmail}
                  className="w-full bg-[#E60000] hover:bg-[#B80000] text-white"
                  data-testid="terminate-email-send-btn"
                >
                  <EnvelopeSimple size={14} weight="bold" className="mr-1.5" />
                  {sendingTerminateEmail ? 'Wird gesendet…' : 'Ja, per E-Mail senden'}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={async () => { await downloadTerminationPdf(); setShowTerminateEmail(false); }}
                disabled={sendingTerminateEmail}
                className="w-full"
                data-testid="terminate-email-download-btn"
              >
                <FileText size={14} weight="bold" className="mr-1.5" />
                Nein, nur PDF herunterladen
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowTerminateEmail(false)}
                disabled={sendingTerminateEmail}
                className="w-full text-[#525866]"
                data-testid="terminate-email-cancel-btn"
              >
                Abbrechen
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      {previewInvoice && <InvoicePreview key={previewInvoice.id} invoice={previewInvoice} onClose={() => setPreviewInvoice(null)} />}
    </div>
  );
};

export default BookingDetail;
